import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import PageCTA from '../../components/feature/PageCTA';

export default function Pricing() {
  // Stripe Price IDs - Add your actual Stripe price IDs here in the morning
  const stripePriceIds = {
    tier1: 'price_TIER1_ID_HERE', // Replace with actual Stripe price ID
    tier2: 'price_TIER2_ID_HERE', // Replace with actual Stripe price ID
    tier3: 'price_TIER3_ID_HERE', // Replace with actual Stripe price ID
    tier4: 'price_TIER4_ID_HERE', // Replace with actual Stripe price ID
    tier5: 'price_TIER5_ID_HERE', // Replace with actual Stripe price ID
  };

  const handleTierSelection = (tierId: string, priceId: string) => {
    // This will redirect to Stripe checkout
    // For now, it's a placeholder - you'll add the actual Stripe checkout logic
    console.log(`Selected tier: ${tierId}, Price ID: ${priceId}`);
    
    // Temporary: Navigate to the tier detail page
    // Once Stripe is connected, this will redirect to Stripe checkout
    window.location.href = `/checkout?tier=${tierId}&priceId=${priceId}`;
  };

  const tiers = [
    {
      id: 'tier1',
      name: 'Quick Pulse™',
      tier: 'Tier 1',
      price: '$299',
      description: 'Fast directional check with one key insight',
      features: [
        'One-page intelligence scan',
        'Risk snapshot',
        'Opportunity indicator',
        'One recommended next step'
      ],
      color: 'from-blue-500 to-cyan-500',
      popular: false,
      priceId: stripePriceIds.tier1
    },
    {
      id: 'tier2',
      name: 'Market Readiness™',
      tier: 'Tier 2',
      price: '$499',
      description: 'Market fit check and risk scan before you invest more',
      features: [
        'Market fit indicator',
        'Red flag scan',
        'Competitive distance score',
        'Opportunity pathway',
        'One strategic upgrade'
      ],
      color: 'from-teal-500 to-emerald-500',
      popular: false,
      priceId: stripePriceIds.tier2
    },
    {
      id: 'tier3',
      name: 'Competitive Intelligence™',
      tier: 'Tier 3',
      price: '$899',
      description: 'Deeper breakdown of your true position against competitors',
      features: [
        'Category standards benchmark',
        'Competitive gap analysis',
        'Feature strength index',
        'Emotional response snapshot',
        'One strategic angle'
      ],
      color: 'from-teal-600 to-blue-600',
      popular: true,
      priceId: stripePriceIds.tier3
    },
    {
      id: 'tier4',
      name: 'Success Blueprint™',
      tier: 'Tier 4',
      price: '$1,499',
      description: 'Strategic roadmap with probability scoring and growth moves',
      features: [
        'Success probability score',
        'Emotional flow resonance',
        'Friction point mapping',
        'Desire trigger scoring',
        'Growth angle blueprint',
        'Two strategic moves'
      ],
      color: 'from-indigo-600 to-purple-600',
      popular: false,
      priceId: stripePriceIds.tier4
    },
    {
      id: 'tier5',
      name: 'Full Success Intelligence™',
      tier: 'Tier 5',
      price: '$2,499',
      description: 'All 7 intelligence systems combined for investor-grade clarity',
      features: [
        'All 7 intelligence systems',
        'Full competitive profile',
        'Global indicators',
        'Full App Credit Score™',
        'Full Validation Passport™',
        'Investor readiness score',
        'Three strategic recommendations'
      ],
      color: 'from-purple-600 to-pink-600',
      popular: false,
      premium: true,
      priceId: stripePriceIds.tier5
    }
  ];

  const comparisonFeatures = [
    { name: 'Intelligence Systems', t1: '1', t2: '2', t3: '4', t4: '6', t5: '7' },
    { name: 'Strategic Recommendations', t1: '1', t2: '1', t3: '1', t4: '2', t5: '3' },
    { name: 'App Credit Score™', t1: false, t2: false, t3: 'Partial', t4: 'Partial', t5: true },
    { name: 'Validation Passport™', t1: false, t2: false, t3: false, t4: 'Partial', t5: true },
    { name: 'Competitive Analysis', t1: false, t2: 'Basic', t3: true, t4: true, t5: true },
    { name: 'Emotional Flow Analysis', t1: false, t2: false, t3: 'Snapshot', t4: true, t5: true },
    { name: 'Global Indicators', t1: false, t2: false, t3: false, t4: false, t5: true },
    { name: 'Investor Readiness Score', t1: false, t2: false, t3: false, t4: false, t5: true },
    { name: 'Delivery Time', t1: '24-48h', t2: '2-3 days', t3: '3-5 days', t4: '5-7 days', t5: '7-10 days' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Choose Your Intelligence Tier
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From quick directional checks to full investor-grade intelligence — pick the level of clarity you need.
          </p>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Tier 1 */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal-500 transition-all duration-300">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Tier 1 – Quick Pulse™</h3>
                <p className="text-gray-600 mb-4">Fast directional check with one key insight</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-gray-900">$299</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">Buy now, pay later options available at checkout</p>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => handleTierSelection('tier1', stripePriceIds.tier1)}
                  className="block w-full bg-teal-500 text-white text-center px-8 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap text-base cursor-pointer"
                >
                  Select Tier 1
                </button>
                <Link
                  to="/tier1-quick-pulse"
                  className="block w-full bg-white text-teal-600 text-center px-8 py-4 rounded-lg font-semibold border-2 border-teal-600 hover:bg-teal-50 transition-colors whitespace-nowrap text-base"
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Tier 2 */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal-500 transition-all duration-300">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Tier 2 – Market Readiness™</h3>
                <p className="text-gray-600 mb-4">Market fit check and risk scan before you invest more</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-gray-900">$499</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">Buy now, pay later options available at checkout</p>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => handleTierSelection('tier2', stripePriceIds.tier2)}
                  className="block w-full bg-teal-500 text-white text-center px-8 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap text-base cursor-pointer"
                >
                  Select Tier 2
                </button>
                <Link
                  to="/tier2-market-readiness"
                  className="block w-full bg-white text-teal-600 text-center px-8 py-4 rounded-lg font-semibold border-2 border-teal-600 hover:bg-teal-50 transition-colors whitespace-nowrap text-base"
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Tier 3 */}
            <div className="bg-white border-2 border-teal-500 rounded-2xl p-8 relative shadow-lg">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-teal-500 text-white px-4 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
                  Most Popular
                </span>
              </div>
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Tier 3 – Competitive Intelligence™</h3>
                <p className="text-gray-600 mb-4">Deeper breakdown of your true position against competitors</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-gray-900">$899</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">Buy now, pay later options available at checkout</p>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => handleTierSelection('tier3', stripePriceIds.tier3)}
                  className="block w-full bg-teal-500 text-white text-center px-8 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap text-base cursor-pointer"
                >
                  Select Tier 3
                </button>
                <Link
                  to="/tier3-competitive-intelligence"
                  className="block w-full bg-white text-teal-600 text-center px-8 py-4 rounded-lg font-semibold border-2 border-teal-600 hover:bg-teal-50 transition-colors whitespace-nowrap text-base"
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Tier 4 */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal-500 transition-all duration-300">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Tier 4 – Success Blueprint™</h3>
                <p className="text-gray-600 mb-4">Strategic roadmap with probability scoring and growth moves</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-gray-900">$1,499</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">Buy now, pay later options available at checkout</p>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => handleTierSelection('tier4', stripePriceIds.tier4)}
                  className="block w-full bg-teal-500 text-white text-center px-8 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap text-base cursor-pointer"
                >
                  Select Tier 4
                </button>
                <Link
                  to="/tier4-success-blueprint"
                  className="block w-full bg-white text-teal-600 text-center px-8 py-4 rounded-lg font-semibold border-2 border-teal-600 hover:bg-teal-50 transition-colors whitespace-nowrap text-base"
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Tier 5 */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-gray-700 rounded-2xl p-8 text-white md:col-span-2 lg:col-span-1">
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">Tier 5 – Full Success Intelligence™</h3>
                <p className="text-gray-300 mb-4">All 7 intelligence systems combined for investor-grade clarity</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">$2,499</span>
                </div>
                <p className="text-sm text-gray-400 mt-2">Buy now, pay later options available at checkout</p>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => handleTierSelection('tier5', stripePriceIds.tier5)}
                  className="block w-full bg-teal-500 text-white text-center px-8 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap text-base cursor-pointer"
                >
                  Select Tier 5
                </button>
                <Link
                  to="/tier5-full-success-intelligence"
                  className="block w-full bg-white text-gray-900 text-center px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap text-base"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>

          {/* Calculator CTA */}
          <div className="text-center mb-16">
            <Link
              to="/tier-comparison"
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calculator-line text-xl"></i>
              Open Investment Calculator
            </Link>
            <p className="text-sm text-gray-600 mt-3">
              Explore tiers and add-ons with real-time pricing
            </p>
          </div>

          {/* Trust Section */}
          <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-2xl p-12 border border-teal-200">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                CrowdRounds Is Trusted By Founders Across
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6 text-center">
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg">
                  <i className="ri-robot-line text-2xl text-teal-600"></i>
                </div>
                <span className="text-sm font-semibold text-gray-800">AI Apps</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg">
                  <i className="ri-code-box-line text-2xl text-teal-600"></i>
                </div>
                <span className="text-sm font-semibold text-gray-800">No-Code Apps</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg">
                  <i className="ri-bank-line text-2xl text-teal-600"></i>
                </div>
                <span className="text-sm font-semibold text-gray-800">FinTech</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg">
                  <i className="ri-heart-pulse-line text-2xl text-teal-600"></i>
                </div>
                <span className="text-sm font-semibold text-gray-800">HealthTech</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg">
                  <i className="ri-dashboard-line text-2xl text-teal-600"></i>
                </div>
                <span className="text-sm font-semibold text-gray-800">Productivity</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg">
                  <i className="ri-book-open-line text-2xl text-teal-600"></i>
                </div>
                <span className="text-sm font-semibold text-gray-800">Education</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg">
                  <i className="ri-heart-line text-2xl text-teal-600"></i>
                </div>
                <span className="text-sm font-semibold text-gray-800">Lifestyle</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Founders Trust CrowdRounds */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* Main Title */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Founders Trust CrowdRounds®
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Finally — validation without guessing. Most founders rely on beta testers, opinions, or hype. 
              CrowdRounds replaces that noise with structured intelligence built on benchmarks, competitive scoring, 
              and multi-layer analysis designed to remove bias and guesswork.
            </p>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto mt-4">
              Whether you're validating an idea or preparing to scale, you get clarity, direction, and real insight.
            </p>
          </div>

          {/* What Makes CrowdRounds Different */}
          <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-12 mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              What Makes CrowdRounds Different
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mt-1"></i>
                <span className="text-gray-800 font-medium">100% objective scoring</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mt-1"></i>
                <span className="text-gray-800 font-medium">No opinions — no popularity — no bias</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mt-1"></i>
                <span className="text-gray-800 font-medium">Benchmarked against global app standards</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mt-1"></i>
                <span className="text-gray-800 font-medium">7 proprietary intelligence systems</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mt-1"></i>
                <span className="text-gray-800 font-medium">Clear, actionable reports</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mt-1"></i>
                <span className="text-gray-800 font-medium">Works for AI apps, no-code apps, early-stage prototypes, and scaling apps</span>
              </div>
              <div className="flex items-start gap-3 md:col-span-2 lg:col-span-3">
                <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mt-1"></i>
                <span className="text-gray-800 font-medium">Built to reveal what investors care about most</span>
              </div>
            </div>
          </div>

          {/* Trusted Across Leading Tech Ecosystems */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Trusted Across Leading Tech Ecosystems
            </h3>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              <div className="flex items-center gap-2 text-gray-700 font-semibold text-lg">
                <i className="ri-apple-fill text-3xl"></i>
                <span>Apple</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700 font-semibold text-lg">
                <i className="ri-google-play-fill text-3xl"></i>
                <span>Google Play</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700 font-semibold text-lg">
                <i className="ri-cloud-fill text-3xl"></i>
                <span>AWS</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700 font-semibold text-lg">
                <i className="ri-meta-fill text-3xl"></i>
                <span>Meta Research</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700 font-semibold text-lg">
                <i className="ri-graduation-cap-fill text-3xl"></i>
                <span>Product School</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700 font-semibold text-lg">
                <i className="ri-code-box-fill text-3xl"></i>
                <span>No-Code Community</span>
              </div>
            </div>
            <p className="text-sm text-gray-500 text-center mt-6 italic">
              This represents ecosystem exposure, not partnership claims.
            </p>
          </div>

          {/* What Founders Say */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-10 text-center">
              What Founders Say
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white border-2 border-teal-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <i className="ri-double-quotes-l text-3xl text-teal-600 mb-3"></i>
                <p className="text-gray-800 font-medium">
                  "CrowdRounds gave me clarity I didn't know I was missing."
                </p>
              </div>
              <div className="bg-white border-2 border-teal-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <i className="ri-double-quotes-l text-3xl text-teal-600 mb-3"></i>
                <p className="text-gray-800 font-medium">
                  "This is the first time feedback actually made sense."
                </p>
              </div>
              <div className="bg-white border-2 border-teal-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <i className="ri-double-quotes-l text-3xl text-teal-600 mb-3"></i>
                <p className="text-gray-800 font-medium">
                  "The intelligence systems showed weaknesses I didn't even know existed."
                </p>
              </div>
              <div className="bg-white border-2 border-teal-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <i className="ri-double-quotes-l text-3xl text-teal-600 mb-3"></i>
                <p className="text-gray-800 font-medium">
                  "Finally — a real way to measure if my app is ready."
                </p>
              </div>
            </div>
          </div>

          {/* Who CrowdRounds Is Built For */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-12 mb-16 text-white">
            <h3 className="text-3xl font-bold mb-8 text-center">
              Who CrowdRounds Is Built For
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <i className="ri-user-star-line text-2xl text-teal-400"></i>
                <span className="font-medium">First-time founders</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-robot-line text-2xl text-teal-400"></i>
                <span className="font-medium">AI & no-code app creators</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-rocket-line text-2xl text-teal-400"></i>
                <span className="font-medium">MVP builders</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-line-chart-line text-2xl text-teal-400"></i>
                <span className="font-medium">Apps struggling with traction</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-funds-line text-2xl text-teal-400"></i>
                <span className="font-medium">Founders preparing for investors</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-team-line text-2xl text-teal-400"></i>
                <span className="font-medium">Teams launching new features</span>
              </div>
              <div className="flex items-center gap-3 md:col-span-2 lg:col-span-3">
                <i className="ri-bar-chart-box-line text-2xl text-teal-400"></i>
                <span className="font-medium">Scale-stage apps needing competitive insight</span>
              </div>
            </div>
          </div>

          {/* What Every Evaluation Includes */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-10 text-center">
              What Every Evaluation Includes
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-teal-50 rounded-xl p-6 text-center">
                <i className="ri-shield-check-line text-4xl text-teal-600 mb-3"></i>
                <p className="text-gray-800 font-semibold">Clear strengths & weaknesses</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-6 text-center">
                <i className="ri-line-chart-line text-4xl text-teal-600 mb-3"></i>
                <p className="text-gray-800 font-semibold">Success probability insights</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-6 text-center">
                <i className="ri-alert-line text-4xl text-teal-600 mb-3"></i>
                <p className="text-gray-800 font-semibold">Red flag & risk indicators</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-6 text-center">
                <i className="ri-trophy-line text-4xl text-teal-600 mb-3"></i>
                <p className="text-gray-800 font-semibold">Competitive distance score</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-6 text-center">
                <i className="ri-compass-line text-4xl text-teal-600 mb-3"></i>
                <p className="text-gray-800 font-semibold">Market alignment clarity</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-6 text-center">
                <i className="ri-brain-line text-4xl text-teal-600 mb-3"></i>
                <p className="text-gray-800 font-semibold">7-system intelligence breakdown</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-6 text-center">
                <i className="ri-map-pin-line text-4xl text-teal-600 mb-3"></i>
                <p className="text-gray-800 font-semibold">Strategic next steps</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-6 text-center">
                <i className="ri-dashboard-line text-4xl text-teal-600 mb-3"></i>
                <p className="text-gray-800 font-semibold">Visual scoring dashboard</p>
              </div>
            </div>
          </div>

          {/* Security & Transparency */}
          <div className="bg-gradient-to-r from-gray-50 to-teal-50 rounded-2xl p-10 mb-16 border-2 border-teal-200">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-600 rounded-full flex-shrink-0">
                <i className="ri-shield-check-fill text-3xl text-white"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Security & Transparency
                </h3>
                <p className="text-lg text-gray-700 mb-3 font-semibold">
                  Your app is safe.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We never ask for login credentials, backend access, or sensitive data. All evaluations are performed using secure, controlled analysis methods.
                </p>
              </div>
            </div>
          </div>

          {/* Flexible Payment Options */}
          <div className="bg-gradient-to-br from-teal-600 to-blue-600 rounded-2xl p-10 text-white text-center">
            <i className="ri-bank-card-line text-5xl mb-4"></i>
            <h3 className="text-2xl font-bold mb-3">
              Flexible Payment Options
            </h3>
            <p className="text-lg mb-2">
              Buy Now, Pay Later is available at checkout for all tiers.
            </p>
            <p className="text-teal-100">
              Cost should never stop you from getting clarity.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <PageCTA 
        title="Ready to Get Started?"
        description="Choose your tier and submit your app for evaluation."
        primaryText="Start Evaluation"
        primaryLink="/submit-app"
      />

      <Footer />
    </div>
  );
}
